/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import { supuesto } from "../../data/ipsos2025";
import { ESCENARIOS_CAPTACION_VESTIMENTA, IPSOS_VESTIMENTA, TICKETS_VESTIMENTA } from "../../data/ipsos2025/vestimenta";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaVestimenta.module.scss";

export const Vestimenta = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  const [vistaActiva, setVistaActiva] = useState("laboral");
  const [marcaSeleccionada, setMarcaSeleccionada] = useState("H&M");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(ESCENARIOS_CAPTACION_VESTIMENTA[1].valor);
  const [ticketPromedio, setTicketPromedio] = useState(TICKETS_VESTIMENTA[1].valor);
  const [verAnual, setVerAnual] = useState(false);

  // Reset marca seleccionada al cambiar de vista
  useEffect(() => {
    if (vistaActiva === "mix") {
      setMarcaSeleccionada("H&M");
    } else {
      const primeraMarca = Object.keys(IPSOS_VESTIMENTA[vistaActiva].marcas)[0];
      setMarcaSeleccionada(primeraMarca);
    }
  }, [vistaActiva]);

  // =====================================================
  // CÁLCULOS POR VISTA (laboral / personal / mix)
  // =====================================================
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    // --- MIX: combina laboral + personal ---
    if (vistaActiva === "mix") {
      const lab = IPSOS_VESTIMENTA.laboral;
      const per = IPSOS_VESTIMENTA.personal;

      // Frecuencia combinada (factor = compras/año, así que comprasAnuales = personas * factor)
      const allFreqs = [...new Set([...Object.keys(lab.frecuenciaCompra), ...Object.keys(per.frecuenciaCompra)])];
      const desgloseFrecuencia = {};
      let totalComprasAnuales = 0;

      allFreqs.forEach((freq) => {
        const lInfo = lab.frecuenciaCompra[freq];
        const pInfo = per.frecuenciaCompra[freq];
        const lPersonas = lInfo ? Math.round(totalEjecutivos * lInfo.pct.valor) : 0;
        const pPersonas = pInfo ? Math.round(totalEjecutivos * pInfo.pct.valor) : 0;
        const lAnuales = lInfo ? Math.round(lPersonas * lInfo.factor.valor) : 0;
        const pAnuales = pInfo ? Math.round(pPersonas * pInfo.factor.valor) : 0;

        desgloseFrecuencia[freq] = {
          label: (lInfo || pInfo).label,
          comprasLabAnuales: lAnuales,
          comprasPerAnuales: pAnuales,
          comprasAnuales: lAnuales + pAnuales,
        };
        totalComprasAnuales += lAnuales + pAnuales;
      });

      // Mercados individuales (anuales) para ponderar marcas
      const labComprasAnuales = Object.entries(lab.frecuenciaCompra).reduce((s, [, info]) => {
        const p = Math.round(totalEjecutivos * info.pct.valor);
        return s + Math.round(p * info.factor.valor);
      }, 0);
      const perComprasAnuales = Object.entries(per.frecuenciaCompra).reduce((s, [, info]) => {
        const p = Math.round(totalEjecutivos * info.pct.valor);
        return s + Math.round(p * info.factor.valor);
      }, 0);
      const labMercadoAnual = labComprasAnuales * ticketPromedio;
      const perMercadoAnual = perComprasAnuales * ticketPromedio;
      const mercadoAnual = totalComprasAnuales * ticketPromedio;
      const mercadoMensual = mercadoAnual / 12;
      const totalComprasMensuales = totalComprasAnuales / 12;

      // Merge marcas (mercado anual por marca) — share resultante no es una pregunta Ipsos
      // directa: promedio ponderado por volumen de compras (laboral+personal), supuesto comercial
      const brandMap = {};
      Object.entries(lab.marcas).forEach(([n, dato]) => {
        brandMap[n] = (brandMap[n] || 0) + Math.round(labMercadoAnual * dato.valor);
      });
      Object.entries(per.marcas).forEach(([n, dato]) => {
        brandMap[n] = (brandMap[n] || 0) + Math.round(perMercadoAnual * dato.valor);
      });
      const marketShareData = Object.entries(brandMap)
        .sort(([, a], [, b]) => b - a)
        .map(([nombre, mercadoAnualMarca]) => {
          const shareValor = mercadoAnual ? mercadoAnualMarca / mercadoAnual : 0;
          return {
            nombre,
            dato: supuesto(shareValor, { nota: "promedio ponderado laboral+personal por volumen de compras" }),
            share: shareValor,
            clientes: mercadoAnual ? Math.round(totalEjecutivos * shareValor) : 0,
            mercadoAnual: mercadoAnualMarca,
            mercadoMensual: Math.round(mercadoAnualMarca / 12),
          };
        });

      // Merge dónde compra (promedio simple laboral+personal, no es una pregunta Ipsos directa)
      const dcMap = {};
      Object.entries(lab.dondeCompra).forEach(([l, dato]) => {
        dcMap[l] = { lab: dato.valor, per: 0 };
      });
      Object.entries(per.dondeCompra).forEach(([l, dato]) => {
        if (dcMap[l]) dcMap[l].per = dato.valor;
        else dcMap[l] = { lab: 0, per: dato.valor };
      });
      const dondeCompra = {};
      Object.entries(dcMap).forEach(([l, { lab: lp, per: pp }]) => {
        const avg = (lp + pp) / 2;
        dondeCompra[l] = {
          dato: supuesto(avg, { nota: "promedio simple laboral+personal" }),
          personas: Math.round(totalEjecutivos * avg),
          pct: avg * 100,
        };
      });

      // Merge canal preferido (promedio simple laboral+personal, no es una pregunta Ipsos directa)
      const canalPreferido = {};
      const allCanales = { ...lab.canalPreferido, ...per.canalPreferido };
      Object.keys(allCanales).forEach((c) => {
        const avg = ((lab.canalPreferido[c]?.valor || 0) + (per.canalPreferido[c]?.valor || 0)) / 2;
        canalPreferido[c] = {
          dato: supuesto(avg, { nota: "promedio simple laboral+personal" }),
          personas: Math.round(totalEjecutivos * avg),
          pct: avg * 100,
        };
      });

      return {
        desgloseFrecuencia,
        totalComprasAnuales,
        totalComprasMensuales,
        mercadoMensual,
        mercadoAnual,
        marketShareData,
        dondeCompra,
        canalPreferido,
        tipoVestimenta: null,
        isMix: true,
      };
    }

    // --- LABORAL / PERSONAL ---
    // factor = compras/año, así que comprasAnuales = personas * factor
    const data = IPSOS_VESTIMENTA[vistaActiva];

    const desgloseFrecuencia = {};
    let totalComprasAnuales = 0;

    Object.entries(data.frecuenciaCompra).forEach(([freq, info]) => {
      const personas = Math.round(totalEjecutivos * info.pct.valor);
      const comprasAnuales = Math.round(personas * info.factor.valor);
      desgloseFrecuencia[freq] = {
        pctDato: info.pct,
        pct: info.pct.valor,
        label: info.label,
        personas,
        comprasAnuales,
      };
      totalComprasAnuales += comprasAnuales;
    });

    const totalComprasMensuales = totalComprasAnuales / 12;
    const mercadoAnual = totalComprasAnuales * ticketPromedio;
    const mercadoMensual = mercadoAnual / 12;

    const marketShareData = Object.entries(data.marcas)
      .sort(([, a], [, b]) => b.valor - a.valor)
      .map(([nombre, dato]) => ({
        nombre,
        dato,
        share: dato.valor,
        clientes: Math.round(totalEjecutivos * dato.valor),
        mercadoAnual: Math.round(mercadoAnual * dato.valor),
        mercadoMensual: Math.round((mercadoAnual * dato.valor) / 12),
      }));

    const dondeCompra = {};
    Object.entries(data.dondeCompra).forEach(([lugar, dato]) => {
      dondeCompra[lugar] = { dato, personas: Math.round(totalEjecutivos * dato.valor), pct: dato.valor * 100 };
    });

    const canalPreferido = {};
    Object.entries(data.canalPreferido).forEach(([canal, dato]) => {
      canalPreferido[canal] = { dato, personas: Math.round(totalEjecutivos * dato.valor), pct: dato.valor * 100 };
    });

    let tipoVestimenta = null;
    if (data.tipoVestimenta) {
      tipoVestimenta = {};
      Object.entries(data.tipoVestimenta).forEach(([tipo, dato]) => {
        tipoVestimenta[tipo] = { dato, personas: Math.round(totalEjecutivos * dato.valor), pct: dato.valor * 100 };
      });
    }

    return {
      desgloseFrecuencia,
      totalComprasAnuales,
      totalComprasMensuales,
      mercadoMensual,
      mercadoAnual,
      marketShareData,
      dondeCompra,
      canalPreferido,
      tipoVestimenta,
      isMix: false,
    };
  }, [totalEjecutivos, ticketPromedio, vistaActiva]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN
  // =====================================================
  const calcularOportunidad = useCallback(
    (marca, pctCaptacion) => {
      if (!resultados) return null;

      const brandData = resultados.marketShareData.find((d) => d.nombre === marca);
      if (!brandData) return null;

      const brandMercadoMensual = brandData.mercadoMensual;
      const brandMercadoAnual = brandData.mercadoAnual;
      const captacionMensual = Math.round(brandMercadoMensual * (pctCaptacion / 100));
      const captacionAnual = Math.round(brandMercadoAnual * (pctCaptacion / 100));

      return {
        mercadoTotal: resultados.mercadoMensual,
        brandShare: brandData.share,
        brandClientes: brandData.clientes,
        brandMercadoMensual,
        captacionMensual,
        captacionAnual,
      };
    },
    [resultados]
  );

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  const isMix = vistaActiva === "mix";
  const dataActiva = isMix ? null : IPSOS_VESTIMENTA[vistaActiva];
  const frecuenciaKeys = isMix
    ? [
        ...new Set([
          ...Object.keys(IPSOS_VESTIMENTA.laboral.frecuenciaCompra),
          ...Object.keys(IPSOS_VESTIMENTA.personal.frecuenciaCompra),
        ]),
      ]
    : Object.keys(dataActiva.frecuenciaCompra);

  return (
    <div className={styles.vestimentaPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>VESTIMENTA</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.vestimentaContent}>
        {/* Tabs Laboral / Personal / Mix */}
        <div className={styles.tabsContainer}>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "laboral" && styles.tabActivo)}
            onClick={() => setVistaActiva("laboral")}
          >
            Laboral
          </button>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "personal" && styles.tabActivo)}
            onClick={() => setVistaActiva("personal")}
          >
            Personal
          </button>
          <button
            type="button"
            className={cx(styles.tab, vistaActiva === "mix" && styles.tabActivo)}
            onClick={() => setVistaActiva("mix")}
          >
            Mix
          </button>
        </div>

        {/* Mercado (toggle mes/año) — primero, controla toda la vista */}
        {resultados && (
          <div className={styles.bloqueMercadoTop}>
            <div className={styles.bloqueMercado}>
              <div className={styles.mercadoLabelRow}>
                <span className={styles.mercadoLabel}>Mercado {verAnual ? "anual" : "mensual"}</span>
                <div className={styles.toggleContainer}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                </div>
              </div>
              <div className={styles.mercadoValorGrande}>
                {formatLocal(convertCLP(verAnual ? resultados.mercadoAnual : resultados.mercadoMensual))}
              </div>
            </div>
          </div>
        )}

        {/* Tabla de frecuencia de compra */}
        {resultados && (
          <div className={cx(styles.tablaContainer, isMix && styles.tablaContainerMix)}>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                {isMix ? (
                  <tr>
                    <th className="text-center">Frecuencia</th>
                    <th className="text-center">Laboral</th>
                    <th className="text-center">Personal</th>
                    <th className="text-center">Total {verAnual ? "año" : "mes"}</th>
                  </tr>
                ) : (
                  <tr>
                    <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de compra</th>
                    <th className={cx("text-center", styles.thPersonas)}>Según frecuencia</th>
                    <th className={cx("text-center", styles.thConversiones)}>
                      Conversiones {verAnual ? "año" : "mes"}
                    </th>
                  </tr>
                )}
              </thead>
              <tbody>
                {frecuenciaKeys.map((freq) => {
                  const data = resultados.desgloseFrecuencia[freq];
                  const toView = (anual) => (verAnual ? anual : anual / 12);
                  if (isMix) {
                    return (
                      <tr key={freq}>
                        <td className={styles.celdaFrecuencia}>{data.label}</td>
                        <td className="text-center">
                          {freq === "noCompra" ? "-" : formatNumber(Math.round(toView(data.comprasLabAnuales)))}
                        </td>
                        <td className="text-center">
                          {data.comprasPerAnuales ? formatNumber(Math.round(toView(data.comprasPerAnuales))) : "-"}
                        </td>
                        <td className="text-center">
                          {freq === "noCompra" ? "-" : formatNumber(Math.round(toView(data.comprasAnuales)))}
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={freq}>
                      <td className={styles.celdaFrecuencia}>
                        <div className={styles.frecuenciaInner}>
                          <BadgeDato dato={data.pctDato}>{(data.pct * 100).toFixed(0)}%</BadgeDato>
                          <span>{data.label}</span>
                        </div>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                      <td className="text-center">
                        {freq === "noCompra" ? "-" : formatNumber(Math.round(toView(data.comprasAnuales)))}
                      </td>
                    </tr>
                  );
                })}
                <tr className={styles.filaTotales}>
                  <td colSpan={isMix ? 3 : 2} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotal)}>
                    <strong>
                      {formatNumber(
                        Math.round(verAnual ? resultados.totalComprasAnuales : resultados.totalComprasMensuales)
                      )}
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Ticket promedio */}
        {resultados && (
          <div className={styles.dosBloquesContainer}>
            <div className={styles.bloqueTicket}>
              <div className={styles.ticketLabel}>Ticket promedio (supuesto)</div>
              <div className={styles.ticketBox}>
                <div className={styles.ticketOpciones}>
                  {TICKETS_VESTIMENTA.map((t) => (
                    <label key={t.valor} className={styles.ticketRadio}>
                      <input
                        type="radio"
                        name="ticketPromedio"
                        checked={ticketPromedio === t.valor}
                        onChange={() => setTicketPromedio(t.valor)}
                      />
                      <span>{formatLocal(convertCLP(t.valor))}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Market Share por Marca */}
        {resultados && (
          <div className={styles.marketShareContainer}>
            <h4 className={styles.marketShareTitulo}>Market Share por Marca</h4>
            <div className={styles.marketShareGrid}>
              {resultados.marketShareData.map((data) => (
                <div key={data.nombre} className={styles.marketShareCard}>
                  <div className={styles.cardNombre}>{data.nombre}</div>
                  <div className={styles.cardShare}>
                    <BadgeDato dato={data.dato}>{(data.share * 100).toFixed(0)}%</BadgeDato>
                  </div>
                  <div className={styles.cardMetricas}>
                    <div>
                      <span className={styles.metricaLabel}>Clientes</span>
                      <span className={styles.metricaValor}>{formatNumber(data.clientes)}</span>
                    </div>
                    <div>
                      <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                      <span className={styles.metricaValor}>
                        {formatLocal(convertCLP(verAnual ? data.mercadoAnual : data.mercadoMensual))}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
              Simular Oportunidad
            </button>
          </div>
        )}

        {/* Tablas de desglose */}
        {resultados && (
          <div className={styles.tablasDesglose}>
            <div className={styles.desgloseContainer}>
              <h4>¿Dónde compra?</h4>
              <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-start">Lugar</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultados.dondeCompra).map(([lugar, data]) => (
                    <tr key={lugar}>
                      <td className="text-start">{lugar}</td>
                      <td className="text-center">
                        <BadgeDato dato={data.dato}>{data.pct.toFixed(0)}%</BadgeDato>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.desgloseContainer}>
              {resultados.tipoVestimenta && !isMix ? (
                <>
                  <h4>Tipo de vestimenta</h4>
                  <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                    <thead className="table-dark">
                      <tr>
                        <th className="text-start">Tipo</th>
                        <th className="text-center">%</th>
                        <th className="text-center">Personas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(resultados.tipoVestimenta).map(([tipo, data]) => (
                        <tr key={tipo}>
                          <td className="text-start">{tipo}</td>
                          <td className="text-center">
                            <BadgeDato dato={data.dato}>{data.pct.toFixed(0)}%</BadgeDato>
                          </td>
                          <td className="text-center">{formatNumber(data.personas)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <>
                  <h4>Canal preferido</h4>
                  <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                    <thead className="table-dark">
                      <tr>
                        <th className="text-start">Canal</th>
                        <th className="text-center">%</th>
                        <th className="text-center">Personas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(resultados.canalPreferido).map(([canal, data]) => (
                        <tr key={canal}>
                          <td className="text-start">{canal}</td>
                          <td className="text-center">
                            <BadgeDato dato={data.dato}>{data.pct.toFixed(0)}%</BadgeDato>
                          </td>
                          <td className="text-center">{formatNumber(data.personas)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        )}

        {/* Canal preferido para laboral y mix (debajo del desglose) */}
        {resultados && (resultados.tipoVestimenta || isMix) && (
          <div className={styles.canalResumen}>
            {Object.entries(resultados.canalPreferido).map(([canal, data]) => (
              <span key={canal}>
                <BadgeDato dato={data.dato}>{data.pct.toFixed(0)}%</BadgeDato> {canal}
                {" — "}
                <strong>{formatNumber(data.personas)}</strong> personas
              </span>
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>
              Ipsos 2025 ({isMix ? "láminas 94 y 102" : vistaActiva === "personal" ? "lámina 94" : "lámina 102"}, base
              400):
            </strong>
            {isMix
              ? " vista combinada laboral + personal."
              : ` vestimenta ${vistaActiva}: el ${vistaActiva === "laboral" ? "39,8%" : "53,6%"} compra mensualmente.`}
          </p>
          <p>
            <strong>Supuestos comerciales:</strong> ticket promedio y compras por año por frecuencia.
            {isMix &&
              " El market share por marca y los promedios de dónde compra/canal preferido son un promedio ponderado laboral+personal, no una pregunta Ipsos directa."}
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && resultados && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  <div className={styles.controlGroup}>
                    <label>Marca objetivo:</label>
                    <select
                      value={marcaSeleccionada}
                      onChange={(e) => setMarcaSeleccionada(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      {(isMix ? resultados.marketShareData.map((d) => d.nombre) : Object.keys(dataActiva.marcas)).map(
                        (m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>% captación mercado (supuesto):</label>
                    <select
                      value={porcentajeCaptacion}
                      onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                      className="form-select form-select-sm"
                    >
                      {ESCENARIOS_CAPTACION_VESTIMENTA.map((p) => (
                        <option key={p.valor} value={p.valor}>
                          {p.valor}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const oportunidad = calcularOportunidad(marcaSeleccionada, porcentajeCaptacion);
                  if (!oportunidad) return null;

                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado total mensual</span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.mercadoTotal))}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>
                          {marcaSeleccionada} — {(oportunidad.brandShare * 100).toFixed(0)}% share (
                          {formatNumber(oportunidad.brandClientes)} clientes)
                        </span>
                        <span className={styles.resultadoValor}>
                          {formatLocal(convertCLP(oportunidad.brandMercadoMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>
                          Captación {porcentajeCaptacion}% de {marcaSeleccionada}
                        </span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.captacionMensual))}/mes
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Oportunidad anual</span>
                        <span className={styles.resultadoValorDestacado}>
                          {formatLocal(convertCLP(oportunidad.captacionAnual))}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vestimenta;
